import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Check, ExternalLink } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { useUiStore } from '@/lib/stores/ui';

export const Route = createFileRoute('/nastavitve/')({
  component: Nastavitve,
});

/* ---- Shared primitives ---- */

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="app-card" style={{ padding: '1.25rem' }}>
      <h3 style={{
        fontFamily: 'var(--font-display)',
        fontWeight: 540,
        fontSize: '1.05rem',
        color: '#fff',
        marginBottom: '1rem',
      }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

type SegOption<T extends string> = { key: T; label: string };

function Segment<T extends string>({
  options, value, onChange,
}: {
  options: SegOption<T>[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="seg-dark">
      {options.map(o => (
        <button
          key={o.key}
          className={value === o.key ? 'active' : ''}
          onClick={() => onChange(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function VaRow({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="va-row">
      <div>
        <div className="va-row__label">{label}</div>
        {hint !== undefined && <div className="va-row__hint">{hint}</div>}
      </div>
      {children}
    </div>
  );
}

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="toggle-switch">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span className="toggle-track" />
      <span className="toggle-thumb" />
    </label>
  );
}

/* ---- 1. Profil obrtnika ---- */

function ProfilSection() {
  const [naziv,  setNaziv]  = useState('Inštalacije Novak');
  const [naslov, setNaslov] = useState('Cesta Andreja Bitenca 9, 4000 Kranj');
  const [ddvId,  setDdvId]  = useState('SI12345678');
  const [trr,    setTrr]    = useState('SI56 0292 0254 4712 314');
  const [tel,    setTel]    = useState('+386 41 234 567');
  const [saved,  setSaved]  = useState(false);

  function handleSave() {
    setSaved(true);
    setTimeout(() => setSaved(false), 2200);
  }

  return (
    <SectionCard title="Profil obrtnika">
      <div className="qform-grid">
        <div className="qfield" style={{ gridColumn: '1 / -1' }}>
          <label>Naziv podjetja</label>
          <input className="qinput" value={naziv} onChange={e => setNaziv(e.target.value)} />
        </div>
        <div className="qfield" style={{ gridColumn: '1 / -1' }}>
          <label>Naslov</label>
          <input className="qinput" value={naslov} onChange={e => setNaslov(e.target.value)} />
        </div>
        <div className="qfield">
          <label>DDV ID / Davčna št.</label>
          <input className="qinput" value={ddvId} onChange={e => setDdvId(e.target.value)} />
        </div>
        <div className="qfield">
          <label>Telefon</label>
          <input className="qinput" value={tel} onChange={e => setTel(e.target.value)} />
        </div>
        <div className="qfield" style={{ gridColumn: '1 / -1' }}>
          <label>TRR (IBAN)</label>
          <input className="qinput" value={trr} onChange={e => setTrr(e.target.value)} />
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
        <button
          onClick={handleSave}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.45rem',
            padding: '0.62rem 1.1rem',
            borderRadius: 'var(--radius-sm)',
            background: saved ? 'rgba(74,222,128,0.15)' : 'var(--copper-500)',
            color: saved ? '#4ADE80' : '#fff',
            border: saved ? '1px solid rgba(74,222,128,0.3)' : '0',
            cursor: 'pointer',
            fontFamily: 'var(--font-body)',
            fontSize: '0.86rem',
            fontWeight: 540,
            transition: 'all var(--dur-base)',
          }}
        >
          {saved && <Check size={13} strokeWidth={2.5} />}
          {saved ? 'Shranjeno' : 'Shrani spremembe'}
        </button>
      </div>
    </SectionCard>
  );
}

/* ---- 2. Glasovni agent ---- */

type Lang = 'SI' | 'IT' | 'EN';
type Redirect = 'takoj' | '15s' | '25s';

const LANG_OPTIONS: SegOption<Lang>[]     = [{ key: 'SI', label: 'SI' }, { key: 'IT', label: 'IT' }, { key: 'EN', label: 'EN' }];
const REDIR_OPTIONS: SegOption<Redirect>[] = [{ key: 'takoj', label: 'Takoj' }, { key: '15s', label: '15s' }, { key: '25s', label: '25s' }];

function AgentSection() {
  const [greeting,   setGreeting]   = useState('Dober dan, tukaj Inštalacije Novak. Marko je trenutno na terenu — kako vam lahko pomagam?');
  const [lang,       setLang]       = useState<Lang>('SI');
  const [termini,    setTermini]    = useState(true);
  const [sms,        setSms]        = useState(true);
  const { agentActive, setAgentActive } = useUiStore();
  const [redirect,   setRedirect]   = useState<Redirect>('25s');

  return (
    <SectionCard title="Glasovni agent">
      <div className="qfield">
        <label>Pozdravno sporočilo</label>
        <textarea
          className="qinput"
          value={greeting}
          onChange={e => setGreeting(e.target.value)}
          rows={3}
        />
      </div>

      <VaRow label="Jezik" hint="Glavni jezik pogovora">
        <Segment options={LANG_OPTIONS} value={lang} onChange={setLang} />
      </VaRow>
      <VaRow label="Agent aktiven" hint="Sprejema dohodne klice">
        <Toggle checked={agentActive} onChange={setAgentActive} />
      </VaRow>
      <VaRow label="Predlaga termine" hint="Iz tvojega urnika">
        <Toggle checked={termini} onChange={setTermini} />
      </VaRow>
      <VaRow label="Pošlji povzetek po SMS" hint="Takoj po klicu">
        <Toggle checked={sms} onChange={setSms} />
      </VaRow>
      <VaRow label="Preusmeri po" hint="Čas preden agent prevzame">
        <Segment options={REDIR_OPTIONS} value={redirect} onChange={setRedirect} />
      </VaRow>
      <VaRow label="Tvoja številka" hint="Klici se preusmerijo na agenta">
        <span className="ct-mono">+386 41 234 567</span>
      </VaRow>
    </SectionCard>
  );
}

/* ---- 3. Delovni čas ---- */

const WORK_HOURS = [
  { day: 'Ponedeljek', hours: '07:00 — 18:00' },
  { day: 'Torek',      hours: '07:00 — 18:00' },
  { day: 'Sreda',      hours: '07:00 — 18:00' },
  { day: 'Četrtek',    hours: '07:00 — 18:00' },
  { day: 'Petek',      hours: '07:00 — 17:00' },
  { day: 'Sobota',     hours: '08:00 — 13:00' },
  { day: 'Nedelja',    hours: null },
];

function DelovniCasSection() {
  return (
    <SectionCard title="Delovni čas">
      {WORK_HOURS.map(({ day, hours }) => (
        <div key={day} className="va-row">
          <div className="va-row__label">{day}</div>
          {hours !== null
            ? <span className="ct-mono">{hours}</span>
            : <span className="pill pill--muted">odzivnik</span>
          }
        </div>
      ))}
      <div style={{ marginTop: '1rem' }}>
        <button style={{
          padding: '0.55rem 1rem',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--app-raised2)',
          border: '1px solid var(--app-line2)',
          color: 'var(--app-text)',
          fontSize: '0.84rem',
          fontFamily: 'var(--font-body)',
          cursor: 'pointer',
        }}>
          Uredi delovni čas
        </button>
      </div>
    </SectionCard>
  );
}

/* ---- 4. Naročnina ---- */

function NarocinaSection() {
  return (
    <SectionCard title="Naročnina">
      {/* Current plan */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '1rem',
        padding: '1rem',
        background: 'var(--app-raised2)',
        border: '1px solid var(--app-line2)',
        borderRadius: 'var(--radius-sm)',
        marginBottom: '1.25rem',
      }}>
        <div>
          <div style={{ fontSize: '1rem', fontWeight: 540, color: '#fff', marginBottom: '0.2rem' }}>
            Profesionalno
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.74rem', color: 'var(--app-muted)' }}>
            19,99 € / mesec · obnavlja se 1. 7. 2026
          </div>
        </div>
        <span className="pill pill--copper pill--dot">aktivno</span>
      </div>

      {/* Usage */}
      <div style={{ marginBottom: '1.1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
          <span className="klabel">AI pogovori danes</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--app-muted)' }}>
            3 / 5
          </span>
        </div>
        <div className="usage-bar">
          <div className="usage-bar__fill" style={{ width: '60%' }} />
        </div>
      </div>
      <div style={{ marginBottom: '1.1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
          <span className="klabel">Ponudbe ta mesec</span>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--app-muted)' }}>
            12 / ∞
          </span>
        </div>
        <div className="usage-bar">
          <div className="usage-bar__fill" style={{ width: '24%', background: 'var(--copper-300)' }} />
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--app-line)', paddingTop: '1rem' }}>
        <a
          href="#"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.45rem',
            padding: '0.62rem 1.1rem',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--app-raised2)',
            border: '1px solid var(--app-line2)',
            color: 'var(--app-text)',
            fontSize: '0.86rem',
            fontFamily: 'var(--font-body)',
            textDecoration: 'none',
          }}
        >
          <ExternalLink size={14} strokeWidth={1.8} />
          Upravljaj naročnino (Stripe)
        </a>
      </div>
    </SectionCard>
  );
}

/* ---- 5. Račun ---- */

function RacunSection() {
  return (
    <SectionCard title="Račun">
      <VaRow label="E-pošta" hint="Prijavljeni ste z">
        <span className="ct-mono">marko.novak@insnov.si</span>
      </VaRow>
      <VaRow label="Geslo" hint="Zadnja sprememba: nikoli">
        <button style={{
          padding: '0.5rem 0.85rem',
          borderRadius: 'var(--radius-sm)',
          background: 'var(--app-raised2)',
          border: '1px solid var(--app-line2)',
          color: 'var(--app-text)',
          fontSize: '0.82rem',
          fontFamily: 'var(--font-body)',
          cursor: 'pointer',
        }}>
          Sprememba gesla
        </button>
      </VaRow>

      <div style={{
        marginTop: '1.25rem',
        paddingTop: '1.25rem',
        borderTop: '1px solid var(--app-line)',
      }}>
        <div className="klabel" style={{ marginBottom: '0.6rem', color: '#F87171' }}>
          Nevarno območje
        </div>
        <button className="danger-btn">Izbriši račun in vse podatke</button>
      </div>
    </SectionCard>
  );
}

/* ---- Page ---- */

function Nastavitve() {
  return (
    <AppShell pageTitle="Nastavitve" crumb="TradeCraft · Nastavitve">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', maxWidth: '680px' }}>
        <ProfilSection />
        <AgentSection />
        <DelovniCasSection />
        <NarocinaSection />
        <RacunSection />
      </div>
    </AppShell>
  );
}
